import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "../../../lib/auth";
import { api } from "../../../lib/api";
import { activitiesApi } from "../../activities/api/activities.api";
import { accountKeys } from "../../account/constants/query-keys";
import { savedApi } from "../../account/api/saved.api";
import {
  readSaved,
  removeSaved,
  toggleSavedItem,
  SAVED_CHANGED_EVENT,
  SAVED_STORAGE_KEY,
  type LocalSavedItem,
} from "../../../shared/hooks/use-local-saved";
import { takePendingSave } from "../../auth/lib/post-sign-in";

/**
 * One heart, one answer.
 *
 * Saving used to live in three places at once. A heart on a card wrote to
 * localStorage even when signed in; a heart on the activity page wrote to the
 * account and nowhere else; the account modal counted the account only and so
 * showed 0 while /account, which merged both, showed something else. Nothing
 * merged on sign-in either, so ten hearts collected as a guest stayed stuck in
 * that one browser forever.
 *
 * The shape is lifted from BucketProvider, which already had this right:
 * localStorage while signed out, and on sign-in push what the browser holds to
 * the account, merge, forget the local copy, then read the account.
 */

type SavedContextValue = {
  /** Slugs currently saved, wherever they live. */
  slugs: Set<string>;
  isSaved: (slug: string) => boolean;
  /** Returns the new state, so a caller can drive its own icon. */
  toggle: (item: Omit<LocalSavedItem, "savedAt">) => Promise<boolean>;
  /** Put the heart where asked, whatever it was. Returns the state it ends in. */
  setSaved: (item: Omit<LocalSavedItem, "savedAt">, saved: boolean) => Promise<boolean>;
  hydrated: boolean;
};

const SavedContext = createContext<SavedContextValue | null>(null);

export function SavedProvider({ children }: { children: ReactNode }) {
  const session = useSession() as { data?: { user?: { id?: string } } | null };
  const userId = session?.data?.user?.id ?? null;
  const queryClient = useQueryClient();

  const [slugs, setSlugs] = useState<Set<string>>(new Set());
  const [hydrated, setHydrated] = useState(false);
  const mergedFor = useRef<string | null>(null);
  // Read by the async calls below, which outlive the render they started in.
  const slugsRef = useRef(slugs);
  slugsRef.current = slugs;

  /**
   * The account's list, plus anything the browser still holds that has not
   * reached the account yet (a sync that failed this time and will be tried
   * again on the next load). Showing only one of the two is what made a heart
   * look empty while the account had it.
   */
  const refreshFromServer = useCallback(async () => {
    const items = await savedApi.listMine();
    const next = new Set(items.map((i) => i.slug));
    for (const local of readSaved()) next.add(local.slug);
    setSlugs(next);
    return items;
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!userId) {
        // Signed out: the browser is the whole story, and a later sign-in will
        // carry it over.
        mergedFor.current = null;
        setSlugs(new Set(readSaved().map((i) => i.slug)));
        setHydrated(true);
        return;
      }

      const local = readSaved();
      if (mergedFor.current !== userId && local.length > 0) {
        try {
          // Cast: /me/saved/sync is off the Eden chain until the api-client
          // types are regenerated, same as the other off-chain routes.
          const res = (await (
            api as unknown as {
              me: { saved: { sync: { post: (b: object) => Promise<{ error?: unknown }> } } };
            }
          ).me.saved.sync.post({ slugs: local.map((i) => i.slug) })) as { error?: unknown };
          if (res?.error) throw new Error("saved sync failed");
          if (cancelled) return;
          // Only cleared once the account has them, so a failed merge leaves
          // the visitor's hearts where they were rather than losing them.
          window.localStorage.removeItem(SAVED_STORAGE_KEY);
          mergedFor.current = userId;
        } catch {
          // Kept for the next load. A failed merge must not also hide the
          // account's own list, which is read next either way.
        }
      } else {
        mergedFor.current = userId;
      }

      try {
        let items = await refreshFromServer();
        if (cancelled) return;
        // The heart tapped while signed out, which opened the sign-in: finish
        // the save it asked for.
        const pending = takePendingSave();
        if (pending && !items.some((i) => i.slug === pending.slug)) {
          await activitiesApi.setWishlist(pending.slug, true);
          items = await refreshFromServer();
          if (cancelled) return;
        }
        void queryClient.invalidateQueries({ queryKey: accountKeys.saved() });
      } catch {
        if (!cancelled) setSlugs(new Set(readSaved().map((i) => i.slug)));
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, refreshFromServer, queryClient]);

  const setSaved = useCallback(
    async (item: Omit<LocalSavedItem, "savedAt">, saved: boolean) => {
      const before = slugsRef.current;
      const wasSaved = before.has(item.slug);
      const next = new Set(before);
      if (saved) next.add(item.slug);
      else next.delete(item.slug);
      slugsRef.current = next;
      setSlugs(next);

      if (!userId) {
        // toggleSavedItem keeps the full card snapshot, which /account renders
        // for signed-out visitors.
        const inBrowser = readSaved().some((i) => i.slug === item.slug);
        if (inBrowser !== saved) toggleSavedItem(item);
        return saved;
      }

      try {
        // The state asked for, not a flip: repeating it (a second tab, a
        // double tap, a list that was stale) cannot undo it.
        await activitiesApi.setWishlist(item.slug, saved);
        // A copy still waiting in the browser for its sync would otherwise
        // come back on the next load after being unsaved here.
        if (!saved) removeSaved(item.slug);
        void queryClient.invalidateQueries({ queryKey: accountKeys.saved() });
        return saved;
      } catch {
        const reverted = new Set(slugsRef.current);
        if (wasSaved) reverted.add(item.slug);
        else reverted.delete(item.slug);
        slugsRef.current = reverted;
        setSlugs(reverted);
        return wasSaved;
      }
    },
    [userId, queryClient],
  );

  const toggle = useCallback(
    (item: Omit<LocalSavedItem, "savedAt">) => setSaved(item, !slugsRef.current.has(item.slug)),
    [setSaved],
  );

  const value = useMemo<SavedContextValue>(
    () => ({ slugs, isSaved: (slug: string) => slugs.has(slug), toggle, setSaved, hydrated }),
    [slugs, toggle, setSaved, hydrated],
  );

  return <SavedContext.Provider value={value}>{children}</SavedContext.Provider>;
}

/**
 * The browser-only behaviour, for a card rendered outside the app shell — a
 * test, a lab, an isolated story. It has to be a hook with its own state and
 * listener rather than a plain read: returning a fresh object each render left
 * the heart frozen after a tap, because nothing told the component to paint
 * again. That is exactly what the card test caught.
 */
function useLocalOnlySaved(): SavedContextValue {
  const [slugs, setSlugs] = useState<Set<string>>(new Set());

  useEffect(() => {
    const sync = () => setSlugs(new Set(readSaved().map((i) => i.slug)));
    sync();
    const onStorage = (e: StorageEvent) => {
      if (e.key === SAVED_STORAGE_KEY) sync();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(SAVED_CHANGED_EVENT, sync as EventListener);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SAVED_CHANGED_EVENT, sync as EventListener);
    };
  }, []);

  const toggle = useCallback(async (item: Omit<LocalSavedItem, "savedAt">) => {
    const nowSaved = toggleSavedItem(item);
    setSlugs(new Set(readSaved().map((i) => i.slug)));
    return nowSaved;
  }, []);

  const setSaved = useCallback(async (item: Omit<LocalSavedItem, "savedAt">, saved: boolean) => {
    if (readSaved().some((i) => i.slug === item.slug) !== saved) toggleSavedItem(item);
    setSlugs(new Set(readSaved().map((i) => i.slug)));
    return saved;
  }, []);

  return useMemo(
    () => ({ slugs, isSaved: (slug: string) => slugs.has(slug), toggle, setSaved, hydrated: true }),
    [slugs, toggle, setSaved],
  );
}

/** The provider when there is one, the browser store when there is not. */
export function useSaved(): SavedContextValue {
  const ctx = useContext(SavedContext);
  // Called unconditionally: hook order cannot depend on whether a provider is
  // mounted above this component.
  const local = useLocalOnlySaved();
  return ctx ?? local;
}
